/**
 * Google Calendar links for Daily Momentum™ activities.
 *
 * Google's "add event" URL takes a single event plus an optional RRULE, so an
 * everyday habit becomes one recurring entry rather than 365 separate ones.
 * Everything here is pure string building — no network, no auth, no account
 * linking. The learner stays in control: the link opens Google's own
 * confirmation screen and nothing lands in their calendar until they say yes.
 */

import type { TaskSchedule } from "@/lib/dailyDiary";

const WD = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Local (floating) timestamp Google reads in the viewer's own time zone. */
function stamp(date: Date): string {
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `T${pad(date.getHours())}${pad(date.getMinutes())}00`
  );
}

function parseYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** The first day this activity should appear on, from `from` onwards. */
export function firstOccurrence(schedule: TaskSchedule, from: Date = new Date()): Date {
  const base = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  if (schedule.kind === "once") return parseYmd(schedule.date) ?? base;
  if (schedule.kind === "weekly") {
    const days = (schedule.days ?? []).filter((d) => d >= 0 && d <= 6);
    if (!days.length) return base;
    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      if (days.includes(d.getDay())) return d;
    }
  }
  return base;
}

/** RRULE for a repeating activity; null for a one-off date. */
export function recurrenceRule(schedule: TaskSchedule): string | null {
  if (schedule.kind === "daily") return "RRULE:FREQ=DAILY";
  if (schedule.kind === "weekly") {
    const days = (schedule.days ?? []).filter((d) => d >= 0 && d <= 6).sort();
    if (!days.length) return "RRULE:FREQ=DAILY";
    return `RRULE:FREQ=WEEKLY;BYDAY=${days.map((d) => WD[d]).join(",")}`;
  }
  return null;
}

export interface CalendarEventInput {
  title: string;
  schedule: TaskSchedule;
  /** 0–23, local. Defaults to 7am — early enough to still count as momentum. */
  hour?: number;
  minutes?: number;
  durationMinutes?: number;
  details?: string;
  from?: Date;
}

/**
 * A Google Calendar "add event" URL for one activity. Opens the confirmation
 * screen in Google, pre-filled and repeating on the learner's own schedule.
 */
export function googleCalendarUrl(input: CalendarEventInput): string {
  const hour = Math.min(23, Math.max(0, Math.round(input.hour ?? 7)));
  const minutes = Math.min(59, Math.max(0, Math.round(input.minutes ?? 0)));
  const duration = Math.max(5, Math.round(input.durationMinutes ?? 30));

  const start = firstOccurrence(input.schedule, input.from);
  start.setHours(hour, minutes, 0, 0);
  const end = new Date(start.getTime() + duration * 60_000);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: input.title,
    dates: `${stamp(start)}/${stamp(end)}`,
    details:
      input.details ??
      "Daily Momentum™ from your WeKIT™ Career Clarity 360 Future Command Center.",
  });

  const rule = recurrenceRule(input.schedule);
  if (rule) params.set("recur", rule);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
